import * as React from "react";
import styled, { keyframes, css } from "styled-components";

export function HeroHeading({ mainText, tagline }) {
  const [isMobile, setIsMobile] = React.useState(window.innerWidth <= 768);

  // Update isMobile state on window resize
  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // This function splits the text into individual letters with spans
  const renderLetters = (text) => {
    return text.split('').map((letter, index) => (
      <LetterSpan key={index} isMobile={isMobile}>
        {letter === ' ' ? '\u00A0' : letter}
      </LetterSpan>
    ));
  };

  return (
    <HeadingWrapper>
      <MainHeading>
        {renderLetters(mainText)}
      </MainHeading>
      <Tagline>{tagline}</Tagline>
    </HeadingWrapper>
  );
}

// Animation for the letter hover effect
const letterPop = keyframes`
  0% {
    transform: scale(1);
    text-shadow: 
      0 1px 0 #ccc,
      0 2px 0 #c9c9c9,
      0 3px 0 #bbb,
      0 4px 0 #b9b9b9,
      0 5px 0 #aaa,
      0 6px 1px rgba(0,0,0,.1),
      0 0 5px rgba(0,0,0,.1),
      0 1px 3px rgba(0,0,0,.3),
      0 3px 5px rgba(0,0,0,.2),
      0 5px 10px rgba(0,0,0,.25);
  }
  50% {
    transform: scale(1.4);
    text-shadow: 
      0 1px 0 #ccc,
      0 2px 0 #c9c9c9,
      0 3px 0 #bbb,
      0 4px 0 #b9b9b9,
      0 5px 0 #aaa,
      0 10px 5px rgba(0,0,0,.1),
      0 0 10px rgba(0,0,0,.1),
      0 1px 5px rgba(0,0,0,.3),
      0 5px 10px rgba(0,0,0,.2),
      0 10px 15px rgba(0,0,0,.25);
  }
  100% {
    transform: scale(1);
    text-shadow: 
      0 1px 0 #ccc,
      0 2px 0 #c9c9c9,
      0 3px 0 #bbb,
      0 4px 0 #b9b9b9,
      0 5px 0 #aaa,
      0 6px 1px rgba(0,0,0,.1),
      0 0 5px rgba(0,0,0,.1),
      0 1px 3px rgba(0,0,0,.3),
      0 3px 5px rgba(0,0,0,.2),
      0 5px 10px rgba(0,0,0,.25);
  }
`;

const HeadingWrapper = styled.div`
  display: flex;
  flex-direction: column;
  color: var(--White, #fff);
  font-weight: 700;
  justify-content: start;
  position: relative;
  z-index: 2;
  @media (max-width: 991px) {
    max-width: 100%;
  }
`;

const MainHeading = styled.h1`
  font-size: 75px;
  margin: 0;
  color: white;
  letter-spacing: -1px;
  position: relative;
  display: flex;
  flex-wrap: wrap;
  
  /* Pure white text */
  text-shadow: 
    0 1px 0 #ccc,
    0 2px 0 #c9c9c9,
    0 3px 0 #bbb,
    0 4px 0 #b9b9b9,
    0 5px 0 #aaa,
    0 6px 1px rgba(0,0,0,.1),
    0 0 5px rgba(0,0,0,.1),
    0 1px 3px rgba(0,0,0,.3),
    0 3px 5px rgba(0,0,0,.2),
    0 5px 10px rgba(0,0,0,.25);
  
  &::after {
    content: "Sell More, Sell Direct";
    position: absolute;
    left: 0;
    top: 2px;
    z-index: -1;
    color: rgba(2, 73, 95, 0.3);
    filter: blur(8px);
  }
  
  &::before {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    top: -10px;
    bottom: -10px;
    background: linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 100%);
    z-index: -2;
    transform: skewX(-15deg);
    pointer-events: none;
  }
  
  @media (max-width: 991px) {
    max-width: 100%;
    font-size: 40px;
    text-shadow: 
      0 1px 0 #ccc,
      0 2px 0 #c9c9c9,
      0 3px 1px rgba(0,0,0,.1),
      0 0 3px rgba(0,0,0,.1),
      0 1px 2px rgba(0,0,0,.3),
      0 2px 3px rgba(0,0,0,.2),
      0 3px 5px rgba(0,0,0,.25);
  }
  @media (max-width: 428px) {
    font-size: 35px;
  }
  @media (max-width: 368px) {
    font-size: 30px;
  }
`;

const LetterSpan = styled.span`
  display: inline-block;
  position: relative;
  transition: transform 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  
  ${props => !props.isMobile && css`
    &:hover {
      animation: ${letterPop} 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275);
      color: #ffffff;
      transform: scale(1.4);
      z-index: 5;
      text-shadow: 
        0 1px 0 #ccc,
        0 2px 0 #c9c9c9,
        0 3px 0 #bbb,
        0 4px 0 #b9b9b9,
        0 5px 0 #aaa,
        0 10px 5px rgba(0,0,0,.1),
        0 0 10px rgba(0,0,0,.1),
        0 1px 5px rgba(0,0,0,.3),
        0 5px 10px rgba(0,0,0,.2),
        0 10px 15px rgba(0,0,0,.25);
    }
  `}
  
  @media (max-width: 768px) {
    padding: 0 1px;
  }
  
  @media (max-width: 480px) {
    padding: 0;
  }
`;

const Tagline = styled.h2`
  font-size: 64px;
  margin: 0;
  background: linear-gradient(135deg, var(--accent-color), #ff9d4a);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  position: relative;
  
  @media (max-width: 991px) {
    max-width: 100%;
    font-size: 40px;
  }
  @media (max-width: 428px) {
    font-size: 35px;
  }
  @media (max-width: 368px) {
    font-size: 30px;
  }
`;
